import type { Document, SourceId } from '@/core/document'
import { ExportUnsupportedError, ExportWorkerError } from '@/core/export/errors'
import { assertExportPlanWithinCanvasLimit } from '@/core/export/planExport'
import {
  ExportMessageType,
  type ExportRequest,
  type ExportResponse,
  type ExportSuccessResponse,
} from '@/core/export/protocol'
import {
  ExportDelivery,
  type ExportArchive,
  type ExportOptions,
  type ExportPlan,
  type ExportResult,
} from '@/core/export/types'
import type { SourceImage } from '@/core/image/SourceImage'
import { probeMaxCanvasArea } from '@/core/render/canvasLimits'

async function requestExport(
  sources: ReadonlyMap<SourceId, SourceImage>,
  document: Document,
  primarySourceId: SourceId,
  plan: ExportPlan,
  options: ExportOptions,
  delivery: ExportDelivery,
): Promise<ExportSuccessResponse> {
  if (typeof OffscreenCanvas === 'undefined') throw new ExportUnsupportedError()
  if (typeof Worker === 'undefined') throw new ExportUnsupportedError('Web Workers')
  if (typeof createImageBitmap === 'undefined') throw new ExportUnsupportedError('createImageBitmap')
  if (plan.outputs.length === 0) throw new Error('Export plan must contain at least one output')
  if (delivery === ExportDelivery.Archive && !document.output.split) {
    throw new Error('Archive export requires a carousel or grid split')
  }
  const maxCanvasArea = await probeMaxCanvasArea()
  assertExportPlanWithinCanvasLimit(plan, maxCanvasArea)

  const primarySource = sources.get(primarySourceId)
  if (!primarySource) throw new Error(`Export primary source "${primarySourceId}" is missing`)
  if (sources.size === 0) throw new Error('Export requires at least one source image')
  const requestSources: ExportRequest['sources'] = []
  try {
    for (const [id, source] of sources) {
      requestSources.push({
        id,
        bitmap: await createImageBitmap(source.bitmap),
        width: source.width,
        height: source.height,
      })
    }
  } catch (error) {
    requestSources.forEach((source) => source.bitmap.close())
    throw error
  }
  const worker = new Worker(new URL('./export.worker.ts', import.meta.url), { type: 'module' })
  const request: ExportRequest = {
    type: ExportMessageType.Request,
    sources: requestSources,
    document,
    fileName: primarySource.fileName,
    plan,
    options,
    delivery,
    maxCanvasArea,
  }

  return new Promise<ExportSuccessResponse>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<ExportResponse>) => {
      worker.terminate()
      if (event.data.type === ExportMessageType.Failure) {
        reject(new ExportWorkerError(event.data.message))
        return
      }
      resolve(event.data)
    }
    worker.onerror = (event) => {
      worker.terminate()
      reject(new ExportWorkerError(event.message || 'Export worker failed'))
    }
    try {
      worker.postMessage(request, requestSources.map((source) => source.bitmap))
    } catch (error) {
      requestSources.forEach((source) => source.bitmap.close())
      worker.terminate()
      reject(error instanceof Error ? error : new Error('Could not start export worker'))
    }
  })
}

export async function runExport(
  sources: ReadonlyMap<SourceId, SourceImage>,
  document: Document,
  primarySourceId: SourceId,
  plan: ExportPlan,
  options: ExportOptions,
): Promise<ExportResult[]> {
  return (await requestExport(sources, document, primarySourceId, plan, options, ExportDelivery.Files)).results
}

export async function runExportArchive(
  sources: ReadonlyMap<SourceId, SourceImage>,
  document: Document,
  primarySourceId: SourceId,
  plan: ExportPlan,
  options: ExportOptions,
): Promise<ExportArchive> {
  const response = await requestExport(sources, document, primarySourceId, plan, options, ExportDelivery.Archive)
  if (!response.archive) throw new Error('Export worker completed without the requested archive')
  return response.archive
}
